// Reads the cells of the first sheet of an Excel file (.xlsx) in the browser, with no library (owner, 2026-10-07: the
// ERP's "ficha operativa" is uploaded as it comes). An .xlsx is a ZIP of XML files: this finds the sheet and the
// shared strings in the ZIP directory, inflates them with the platform's DecompressionStream and reads the cells.
// Defensive by construction: bounded sizes (a "ZIP bomb" is refused), only the two entries needed are inflated, and
// the XML is read as text (no entity expansion, no external references).

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_ENTRY_BYTES = 20 * 1024 * 1024;

export class XlsxError extends Error {}

interface Entry {
  readonly name: string;
  readonly method: number;
  readonly compressedSize: number;
  readonly uncompressedSize: number;
  readonly localOffset: number;
}

function entriesOf(bytes: Uint8Array): Entry[] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // End of central directory: the last record with signature 0x06054b50 (at most 64 KiB of comment after it).
  let end = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 22 - 65_535); i -= 1) {
    if (view.getUint32(i, true) === 0x06054b50) {
      end = i;
      break;
    }
  }
  if (end < 0) throw new XlsxError('no es un archivo de Excel (.xlsx)');
  const count = view.getUint16(end + 10, true);
  let offset = view.getUint32(end + 16, true);
  const entries: Entry[] = [];
  const decoder = new TextDecoder();
  for (let n = 0; n < count; n += 1) {
    if (offset + 46 > bytes.length || view.getUint32(offset, true) !== 0x02014b50) {
      throw new XlsxError('el archivo está dañado');
    }
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    entries.push({
      name: decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength)),
      method: view.getUint16(offset + 10, true),
      compressedSize: view.getUint32(offset + 20, true),
      uncompressedSize: view.getUint32(offset + 24, true),
      localOffset: view.getUint32(offset + 42, true),
    });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

async function inflate(bytes: Uint8Array, entry: Entry): Promise<string> {
  if (entry.uncompressedSize > MAX_ENTRY_BYTES) throw new XlsxError('el archivo es demasiado grande');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(entry.localOffset, true) !== 0x04034b50) throw new XlsxError('el archivo está dañado');
  const start =
    entry.localOffset +
    30 +
    view.getUint16(entry.localOffset + 26, true) +
    view.getUint16(entry.localOffset + 28, true);
  const data = bytes.subarray(start, start + entry.compressedSize);
  if (entry.method === 0) return new TextDecoder().decode(data);
  if (entry.method !== 8) throw new XlsxError('formato de compresión no soportado');
  const stream = new Blob([new Uint8Array(data)]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    // The declared size can lie: the real output is bounded too.
    if (total > MAX_ENTRY_BYTES) {
      await reader.cancel();
      throw new XlsxError('el archivo es demasiado grande');
    }
    chunks.push(value);
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.byteLength;
  }
  return new TextDecoder().decode(out);
}

function decodeXml(text: string): string {
  return text.replace(/&(#x[0-9a-fA-F]+|#\d+|amp|lt|gt|quot|apos);/g, (_, code: string) => {
    if (code === 'amp') return '&';
    if (code === 'lt') return '<';
    if (code === 'gt') return '>';
    if (code === 'quot') return '"';
    if (code === 'apos') return "'";
    const point = code.startsWith('#x') ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
    return Number.isFinite(point) && point > 0 && point <= 0x10ffff ? String.fromCodePoint(point) : '';
  });
}

/** All the text runs of an `<si>` / `<is>` element, concatenated. */
const textOf = (xml: string): string =>
  [...xml.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)].map((match) => decodeXml(match[1] ?? '')).join('');

/** "AB21" ⇒ column index 27 (0-based) and row 21. */
function cellPosition(reference: string): { column: number; row: number } | undefined {
  const match = /^([A-Z]{1,3})(\d+)$/.exec(reference);
  if (match === null) return undefined;
  let column = 0;
  for (const letter of match[1] ?? '') column = column * 26 + (letter.charCodeAt(0) - 64);
  return { column: column - 1, row: Number(match[2]) };
}

/**
 * The cells of the first worksheet as text, row by row (index 0 = row 1 of the sheet). Empty cells are ''. Numbers keep
 * their written form; dates come as Excel serial numbers (not used by the import).
 */
export async function readXlsxRows(file: Uint8Array): Promise<string[][]> {
  if (file.byteLength > MAX_FILE_BYTES) throw new XlsxError('el archivo es demasiado grande');
  const entries = entriesOf(file);
  const sheets = entries
    .filter((entry) => /^xl\/worksheets\/sheet\d+\.xml$/.test(entry.name))
    .sort((a, b) => Number(/\d+/.exec(a.name)?.[0]) - Number(/\d+/.exec(b.name)?.[0]));
  const sheet = sheets[0];
  if (sheet === undefined) throw new XlsxError('el archivo no tiene hojas');
  const sharedEntry = entries.find((entry) => entry.name === 'xl/sharedStrings.xml');
  const shared =
    sharedEntry === undefined
      ? []
      : [...(await inflate(file, sharedEntry)).matchAll(/<si>([\s\S]*?)<\/si>/g)].map((match) =>
          textOf(match[1] ?? ''),
        );
  const xml = await inflate(file, sheet);
  const rows: string[][] = [];
  for (const match of xml.matchAll(/<c\s([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
    const attributes = match[1] ?? '';
    const body = match[2] ?? '';
    const position = cellPosition(/\br="([A-Z]+\d+)"/.exec(attributes)?.[1] ?? '');
    if (position === undefined || position.row > 100_000 || position.column > 200) continue;
    const type = /\bt="([^"]+)"/.exec(attributes)?.[1];
    const raw = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1];
    let value = '';
    if (type === 's' && raw !== undefined) value = shared[Number(raw)] ?? '';
    else if (type === 'inlineStr') value = textOf(body);
    else if (raw !== undefined) value = decodeXml(raw);
    const row = (rows[position.row - 1] ??= []);
    while (row.length < position.column) row.push('');
    row[position.column] = value;
  }
  for (let i = 0; i < rows.length; i += 1) rows[i] ??= [];
  return rows;
}
